import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import {
    IOneVerifyCreateSavedFilterData,
    IOneVerifyGetSavedFiltersListData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types"
import { IOneVerifyReportSavedFilter } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { API_ROUTES } from "../config/routes.config"
import { AmplitudeHelper } from "../helpers/amplitude.helper"
import { HttpHelper } from "../helpers/http.helper"
import { PoliciesHelper } from "../helpers/policies.helper"
import { UtilHelper } from "../helpers/util.helper"
import { PoliciesService } from "./policies.service"

export class ReportSavedFiltersService {
    /**
     * Get list of saved filters
     */
    static async getList(
        practice: IPractice,
        reportType: PoliciesListType,
    ): Promise<IOneVerifyReportSavedFilter[] | undefined> {
        const requestData: IOneVerifyGetSavedFiltersListData = {
            reportType,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.SAVED_FILTERS_GET_LIST.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Create saved filter
     */
    static async create(
        practice: IPractice,
        data: IOneVerifyCreateSavedFilterData,
    ): Promise<IOneVerifyReportSavedFilter | undefined> {
        const {
            historyMode: _historyMode,
            payerId: _payerId,

            ...filtersConfiguration
        } = PoliciesService.mapPoliciesFilters(
            data.reportType,
            data.filtersConfiguration,
            false,
        )

        const result = await HttpHelper.sendRequest(
            API_ROUTES.SAVED_FILTERS_CREATE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            {
                ...data,

                filtersConfiguration: {
                    ...filtersConfiguration,

                    payer: data.filtersConfiguration.payer,
                },
            },
            "POST",
        )

        AmplitudeHelper.logEvent(AmplitudeEvent.SAVED_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            reportType: data.reportType,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(
                data.filtersConfiguration,
            ),
        })

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Delete saved filter
     */
    static async delete(
        practice: IPractice,
        filterId: number,
    ): Promise<
        | {
              deletedId: number
          }
        | undefined
    > {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.SAVED_FILTERS_DELETE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":filterId", filterId.toString()),
            undefined,
            "DELETE",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }
}
